import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  User, Mail, GraduationCap, CreditCard, LogOut, Atom, 
  CheckCircle, XCircle, Clock, AlertCircle, RefreshCw,
  FileText, Upload, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StemBackgroundDecorations from '../components/StemBackgroundDecorations';

// Safe localStorage wrapper
const safeStorage = {
  getItem: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { localStorage.setItem(key, value); } catch { console.warn('Storage unavailable'); } },
  removeItem: (key) => { try { localStorage.removeItem(key); } catch { console.warn('Storage unavailable'); } }
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    const token = safeStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }

    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const token = safeStorage.getItem('studentToken');
      const res = await axios.get(`${API}/students/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudent(res.data.student);
      setPayments(res.data.payments || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    safeStorage.removeItem('studentToken');
    safeStorage.removeItem('studentId');
    safeStorage.removeItem('studentName');
    safeStorage.removeItem('studentEmail');
    navigate('/student/login');
  };

  const getStatusBadge = (status) => {
    const styles = {
      verified: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle, label: 'Approved' },
      pending_verification: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pending' },
      rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Rejected' },
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pending' }
    };
    return styles[status] || styles.pending;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-SG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-deep-blue">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-deep-blue overflow-hidden">
      <StemBackgroundDecorations />
      
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <header className="bg-deep-navy/80 border-b border-surface-navy" style={{ backdropFilter: 'blur(12px)' }}>
          <div className="container mx-auto px-4 md:px-6 max-w-6xl">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-electric-cyan/20 flex items-center justify-center">
                  <Atom className="w-5 h-5 text-electric-cyan" />
                </div>
                <span className="font-outfit font-bold text-lg text-white">
                  Physics<span className="text-electric-cyan">Co</span>
                </span>
              </Link>
              
              <div className="flex items-center gap-4">
                <span className="text-slate-400 text-sm hidden sm:block">
                  Welcome, {student?.full_name?.split(' ')[0]}
                </span>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-white"
                  data-testid="student-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 md:px-6 max-w-6xl py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-2" data-testid="student-dashboard-title">
              Student Dashboard
            </h1>
            <p className="text-slate-400 mb-8">Manage your profile and track your enrollment</p>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Profile Card */}
              <div className="md:col-span-1">
                <div className="glass-card p-6" data-testid="student-profile-card">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-electric-cyan/20 flex items-center justify-center">
                      <User className="w-8 h-8 text-electric-cyan" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-lg font-semibold text-white">{student?.full_name}</h2>
                      <p className="text-slate-400 text-sm">{student?.grade}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-slate-500 text-xs">Email</p>
                        <p className="text-white text-sm">{student?.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <GraduationCap className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-slate-500 text-xs">Course</p>
                        <p className="text-white text-sm">{student?.course_type || '-'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-slate-500 text-xs">Subjects</p>
                        <p className="text-white text-sm">{student?.subjects?.join(', ') || '-'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-slate-500 text-xs">Overall Payment Status</p>
                        {(() => {
                          const status = getStatusBadge(student?.payment_status);
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                              <status.icon className="w-3 h-3" />
                              {status.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payments Section */}
              <div className="md:col-span-2">
                <div className="glass-card p-6" data-testid="student-payments-card">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-electric-cyan" />
                      Payment History
                    </h2>
                    <Button
                      onClick={() => navigate('/payment')}
                      className="btn-primary text-sm"
                      data-testid="make-payment-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Submit Payment
                    </Button>
                  </div>

                  {payments.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-navy/50 flex items-center justify-center">
                        <CreditCard className="w-8 h-8 text-slate-500" />
                      </div>
                      <p className="text-slate-400 mb-2">No payment submissions yet</p>
                      <p className="text-slate-500 text-sm mb-4">Submit your payment proof to complete enrollment</p>
                      <Button onClick={() => navigate('/payment')} className="btn-secondary">
                        Submit Payment Proof
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {payments.map((payment, idx) => {
                        const status = getStatusBadge(payment.payment_status);
                        const StatusIcon = status.icon;
                        return (
                          <motion.div
                            key={payment.id || idx}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="bg-surface-navy/50 rounded-xl p-4 border border-surface-navy/50"
                            data-testid={`payment-item-${idx}`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="text-white font-medium">{payment.course_name || 'Course Payment'}</h3>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {status.label}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-4 text-sm">
                                  <span className="text-slate-400">
                                    <span className="text-electric-cyan font-semibold">SGD {payment.amount}</span>
                                  </span>
                                  <span className="text-slate-500 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(payment.submitted_at)}
                                  </span>
                                  <span className="text-slate-500 capitalize">
                                    {payment.payment_method === 'bank_transfer' ? 'Bank Transfer' : 'PayNow'}
                                  </span>
                                </div>
                                
                                {/* Admin remarks for rejected payments */}
                                {payment.payment_status === 'rejected' && payment.admin_remarks && (
                                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <p className="text-red-400 text-sm flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                      <span><strong>Rejection reason:</strong> {payment.admin_remarks}</span>
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Re-upload option for rejected payments */}
                              {payment.payment_status === 'rejected' && (
                                <Button
                                  onClick={() => navigate('/payment')}
                                  className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                  size="sm"
                                  data-testid={`reupload-btn-${idx}`}
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Re-upload Proof
                                </Button>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="glass-card p-6 mt-6">
                  <h2 className="font-outfit text-lg font-semibold text-white mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <Link
                      to="/courses"
                      className="p-4 bg-surface-navy/50 rounded-xl hover:bg-surface-navy/70 transition-colors text-center"
                    >
                      <GraduationCap className="w-6 h-6 text-electric-cyan mx-auto mb-2" />
                      <span className="text-white text-sm">View Courses</span>
                    </Link>
                    <Link
                      to="/payment"
                      className="p-4 bg-surface-navy/50 rounded-xl hover:bg-surface-navy/70 transition-colors text-center"
                    >
                      <Upload className="w-6 h-6 text-electric-cyan mx-auto mb-2" />
                      <span className="text-white text-sm">Submit Payment</span>
                    </Link>
                    <Link
                      to="/contact"
                      className="p-4 bg-surface-navy/50 rounded-xl hover:bg-surface-navy/70 transition-colors text-center"
                    >
                      <Mail className="w-6 h-6 text-electric-cyan mx-auto mb-2" />
                      <span className="text-white text-sm">Contact Support</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default StudentDashboard;
